import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Download, FileText, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkImport = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }, []);

  const handleFile = (selectedFile) => {
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    setFile(selectedFile);
    setResults(null);
  };

  const handleImport = async () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/contracts/import/csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResults(response.data);
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} contracts`);
      }
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API}/contracts/import/csv-template`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'contract_import_template.csv';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success("Template downloaded");
    } catch {
      toast.error("Failed to download template");
    }
  };

  return (
    <div className="animate-fadeIn" data-testid="bulk-import-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span>&rsaquo;</span>
        <span>Bulk Import</span>
      </div>

      <div className="page-header">
        <h1 className="page-title">Bulk Import from CSV</h1>
        <p className="page-subtitle">Import multiple contracts at once using a CSV file</p>
      </div>

      <div className="form-card">
        <div className="form-body-inner">
          {/* Step 1: Download Template */}
          <div style={{ 
            display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px',
            background: 'var(--gold-pale)', border: '1px solid var(--gold)', borderRadius: 'var(--radius)', marginBottom: '20px'
          }}>
            <FileText className="w-6 h-6" style={{ color: 'var(--amber)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, color: 'var(--amber)' }}>Step 1: Download the CSV template</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Fill in your contract data using the provided template with sample rows
              </div>
            </div>
            <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-csv-template-btn"
              style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}>
              <Download className="w-4 h-4 mr-2" /> Download Template
            </Button>
          </div>

          {/* Step 2: Upload */}
          <div style={{ marginBottom: '8px', fontWeight: 500, color: 'var(--navy)' }}>Step 2: Upload your filled CSV</div>
          <div
            className={`upload-zone ${dragActive ? 'dragging' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('csv-input').click()}
            data-testid="csv-upload-dropzone"
          >
            <input
              id="csv-input"
              type="file"
              accept=".csv"
              onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
              style={{ display: 'none' }}
            />
            {file ? (
              <>
                <div className="upload-icon"><FileText className="w-10 h-10" style={{ color: 'var(--navy)' }} /></div>
                <p className="upload-text"><strong>{file.name}</strong></p>
                <p style={{ fontSize: '12px', color: 'var(--text-light)' }}>{(file.size / 1024).toFixed(1)} KB</p>
              </>
            ) : (
              <>
                <div className="upload-icon"><Upload className="w-10 h-10" style={{ color: 'var(--text-light)' }} /></div>
                <p className="upload-text"><strong>Click to upload</strong> or drag and drop</p>
                <p style={{ fontSize: '12px', color: 'var(--text-light)' }}>CSV files only</p>
              </>
            )}
          </div>

          {file && !results && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <Button onClick={handleImport} disabled={uploading} data-testid="import-csv-btn"
                style={{ background: 'var(--navy)', color: '#fff', padding: '10px 32px' }}>
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? 'Importing...' : 'Import Contracts'}
              </Button>
            </div>
          )}

          {/* Results */}
          {results && (
            <div style={{ marginTop: '24px' }} data-testid="import-results">
              {/* Summary */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                <div style={{ 
                  flex: 1, padding: '16px', borderRadius: 'var(--radius)', textAlign: 'center',
                  background: 'var(--green-pale)', border: '1px solid var(--green)'
                }}>
                  <CheckCircle className="w-6 h-6" style={{ color: 'var(--green)', margin: '0 auto 8px' }} />
                  <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--green)' }}>{results.imported}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Imported</div>
                </div>
                {results.errors?.length > 0 && (
                  <div style={{ 
                    flex: 1, padding: '16px', borderRadius: 'var(--radius)', textAlign: 'center',
                    background: 'var(--red-pale)', border: '1px solid var(--red)'
                  }}>
                    <AlertCircle className="w-6 h-6" style={{ color: 'var(--red)', margin: '0 auto 8px' }} />
                    <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--red)' }}>{results.errors.length}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Errors</div>
                  </div>
                )}
              </div>

              {/* Imported Contracts */}
              {results.contracts?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontWeight: 500, marginBottom: '8px', color: 'var(--green)' }}>Successfully Imported</div>
                  <div style={{ border: '1px solid var(--gray-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                    <table className="contract-table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Work Order No.</th>
                          <th>Vendor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.contracts.map((c, i) => (
                          <tr key={i} style={{ cursor: 'default' }}>
                            <td>{c.row}</td>
                            <td><span className="contract-num">{c.contract_number}</span></td>
                            <td>{c.vendor}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {results.errors?.length > 0 && (
                <div>
                  <div style={{ fontWeight: 500, marginBottom: '8px', color: 'var(--red)' }}>Errors</div>
                  {results.errors.map((err, i) => (
                    <div key={i} style={{ padding: '8px 12px', background: 'var(--red-pale)', borderRadius: 'var(--radius-sm)', marginBottom: '4px', fontSize: '13px' }}>
                      <strong>Row {err.row}:</strong> {err.error}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px' }}>
                <Button variant="outline" onClick={() => { setFile(null); setResults(null); }}>
                  Import More
                </Button>
                <Button onClick={() => navigate('/contracts')} style={{ background: 'var(--navy)', color: '#fff' }}
                  data-testid="view-contracts-btn">
                  View All Contracts
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImport;
